import moment from 'moment';
import Stock from '../../../model/Stock';
import { GlanceItem } from '../../../model/DashboardOverviewResult';

// Dashboard › Analytics › Export Board Report (Figma 460:4494 / 482:1434) and the printable investor report it opens
// (/analytics-report/{ticker:exchange}/{quarter}?s=…, Figma 482:1902 / 482:1934 / 484:1562 / 484:1755).
// Everything here works from GET /api/dashboard/analytics (the same AnalyticsResult the Analytics tab shows).

// "2026-Q3" → "Jul–Sep 2026"
export const quarterLabel = (q: string) => {
    const [y, n] = q.split('-Q').map(Number);
    const start = moment({ year: y, month: (n - 1) * 3, day: 1 });
    return `${start.format('MMM')}–${start.clone().add(2, 'months').format('MMM YYYY')}`;
};

// "2026-Q3" → "Sep qtr"; back = quarters earlier
export const qtrLabel = (q: string, back = 0) => {
    const [y, n] = q.split('-Q').map(Number);
    return moment({ year: y, month: n * 3 - 1, day: 1 }).subtract(back * 3, 'months').format('MMM') + ' qtr';
};

// "2026-Q3" → "1 July – 30 September 2026"; the current quarter ends today ("… (to date)")
export const quarterRange = (q: string, current: boolean) => {
    const [y, n] = q.split('-Q').map(Number);
    const start = moment({ year: y, month: (n - 1) * 3, day: 1 });
    const end = current ? moment() : start.clone().add(3, 'months').subtract(1, 'day');
    return `${start.format('D MMMM')} – ${end.format('D MMMM YYYY')}${current ? ' (to date)' : ''}`;
};

export type SectionKey = 'overview' | 'announcements' | 'investors' | 'email' | 'traffic';

export const SECTIONS: { key: SectionKey; title: string }[] = [
    { key: 'overview', title: 'Engagement overview' },
    { key: 'announcements', title: 'Announcement impact (opens, clicks, website visits, price move)' },
    { key: 'investors', title: 'Investor growth and sign‑ups' },
    { key: 'email', title: 'Email campaign performance' },
    { key: 'traffic', title: 'Website pages and traffic sources' },
];

export const DEFAULT_SECTIONS: SectionKey[] = ['overview', 'announcements', 'investors', 'traffic']; // Figma: email unticked

const isSection = (s: string): s is SectionKey => SECTIONS.some(x => x.key === s);

export const parseSections = (search: string): SectionKey[] => {
    const raw = new URLSearchParams(search).get('s');
    if (raw === null) return DEFAULT_SECTIONS;
    return raw.split(',').filter(isSection);
};

export const analyticsReportUrl = (stock: Stock, quarter: string, sections: SectionKey[], print = false) =>
    `/analytics-report/${stock.ticker}:${stock.exchange}/${quarter}?s=${sections.join(',')}${print ? '&print=1' : ''}`;

export const isQuarter = (q: string) => /^\d{4}-Q[1-4]$/.test(q);

// sheets after the cover, in order; the announcement page carries the email bars, the investor page the traffic sources
export type PageKey = 'cover' | 'glance' | 'announcements' | 'investors';
export const reportPages = (sections: SectionKey[]): PageKey[] => {
    const has = (k: SectionKey) => sections.includes(k);
    return [
        'cover' as PageKey,
        ...(has('overview') ? ['glance' as PageKey] : []),
        ...(has('announcements') || has('email') ? ['announcements' as PageKey] : []),
        ...(has('investors') || has('traffic') ? ['investors' as PageKey] : []),
    ];
};

export const reportFileName = (stock: Stock, quarter: string) =>
    `${(stock.companyName || stock.ticker).replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '')}-Investor-Report-${qtrLabel(quarter).replace(' qtr', '')}-${quarter.slice(0, 4)}.pdf`;

// ---- reading the key results (AnalyticsResult.keyResults, text built by AnalyticsBuilder) ----

const toNumber = (s: string) => Number(s.replace(/[,%+\s]/g, '').replace('−', '-'));

export type Trend = 'up' | 'down' | 'steady';

// direction against the previous quarter, from the figures the result already carries:
// "… · vs 38.4% previous period" (rates, registry growth) or a trailing "· +22%" / "· −8.5%" (views, visitors).
// null when the result has no previous-quarter figure (e.g. concentration), so no badge is shown.
export const trend = (item: GlanceItem): Trend | null => {
    const vs = item.detail.match(/vs ([+−-]?[\d.,]+)% previous period/);
    if (vs) {
        const now = toNumber(item.value);
        const before = toNumber(vs[1]);
        if (isNaN(now) || isNaN(before)) return null;
        if (Math.abs(now - before) < 0.05) return 'steady';
        return now > before ? 'up' : 'down';
    }
    const tail = item.detail.match(/· ([+−-][\d.,]+)%$/);
    if (tail) {
        const change = toNumber(tail[1]);
        if (isNaN(change)) return null;
        if (Math.abs(change) < 0.05) return 'steady';
        return change > 0 ? 'up' : 'down';
    }
    return null;
};

export const findResult = (items: GlanceItem[], label: string) => items.find(x => x.label === label) ?? null;

// "772 holders · +24 net new · …" → "+24"
export const netNew = (registry: GlanceItem | null) => registry?.detail.match(/([+−-]?[\d,]+) net new/)?.[1] ?? null;
// "1,210 signed in · 46 new sign-ups · …" → "46"
export const signUps = (visitors: GlanceItem | null) => visitors?.detail.match(/([\d,]+) new sign-ups?/)?.[1] ?? null;

export const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
